import { z } from "zod";

// TES-1040 — support access: the documented PDL basis on which an admin reads
// a patient's data for debugging or technical support.
//
// Simon 2026-10-01: an admin may see what the doctor and the coach see, read
// only. The first time an admin opens a patient's page or patient-scoped
// data, she states a short reason. The reason is the access basis for that
// admin and that patient until the end of the Stockholm calendar day.
//
// What follows from it, server side:
//   - every patient-scoped employee read by an admin without an active grant
//     answers 403 `support_reason_required` (below);
//   - every read under a grant writes the normal PDL access-log row with the
//     basis `support`, never a care relationship;
//   - `support` is a documented basis, so it raises no access alarm
//     (TES-1003, Ö-261: "tidsbegränsad dokumenterad supportåtkomst");
//   - the access review lists every grant.
//
// The admin never acts as a clinician: the routes opened to her are GETs, and
// every mutating route keeps its doctor/coach-only roles.

/** The access-log basis a read under a support grant records. */
export const SUPPORT_ACCESS_BASIS = "support" as const;

export const SUPPORT_REASON_MIN_LENGTH = 5;
export const SUPPORT_REASON_MAX_LENGTH = 500;

// POST employee-api/v1/patients/:id/support-access
//
// Trimmed before the length check, so a field of spaces cannot pass for a
// reason. Five characters is enough for a ticket number ("TES-1") and too
// little for "a" or "test".
export const grantSupportAccessSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(SUPPORT_REASON_MIN_LENGTH)
    .max(SUPPORT_REASON_MAX_LENGTH),
});
export type GrantSupportAccessRequest = z.infer<
  typeof grantSupportAccessSchema
>;

export const supportAccessGrantedSchema = z.object({
  /** End of the Stockholm calendar day the grant was given on (ISO). */
  expiresAt: z.string(),
});
export type SupportAccessGranted = z.infer<typeof supportAccessGrantedSchema>;

// GET employee-api/v1/patients/:id/support-access — does the calling admin
// hold an active grant for this patient? Answers without reading anything
// about her, so the admin UI can decide whether to show the reason dialog.
export const supportAccessStatusSchema = z.object({
  active: z.boolean(),
  expiresAt: z.string().nullable(),
});
export type SupportAccessStatus = z.infer<typeof supportAccessStatusSchema>;

// The 403 a patient-scoped admin read answers without an active grant. The
// admin UI shows the reason dialog on exactly this error code.
export const SUPPORT_REASON_REQUIRED_ERROR = "support_reason_required" as const;
export const SUPPORT_REASON_REQUIRED_MESSAGE_KEY =
  "errors.support_access.reason_required" as const;

// Which clinician's view an admin is reading. The doctor and the coach get
// differently shaped answers from the same routes (the work queue's lanes,
// the check-in trend, the cycle view, the F-01 start context), and the role
// that picks the shape normally comes from the JWT. An admin has neither
// role, so the admin UI says which menu section she is in with this header.
//
// It is honoured for the admin role only. A doctor or a coach always gets
// her own role's shape, whatever the header says — a client-supplied role is
// not an authorisation decision (TES-648/651), and admin is already allowed
// both shapes.
export const SUPPORT_VIEW_AS_HEADER = "x-support-view-as" as const;
export const SUPPORT_VIEW_ROLES = ["doctor", "coach"] as const;
export type SupportViewRole = (typeof SUPPORT_VIEW_ROLES)[number];
/** The view an admin gets when the header is missing or unknown. */
export const DEFAULT_SUPPORT_VIEW_ROLE: SupportViewRole = "doctor";

// GET employee-api/v1/access-review/support-access — the grants in a window,
// for the access review.
//
// The patient is an opaque id, as everywhere in the review (access-review.ts:
// "Staff are named, the patient is not").
//
// The reason IS returned, in plain text (Simon 2026-10-01, TES-1040): it is
// the admin's justification for looking, and a review that cannot read it
// cannot judge it. It is decrypted under the patient's data key for the
// reviewer, and every listing that decrypts reasons writes one
// `support_access.reason_viewed` audit row per grant (grant id only). A
// patient whose data was erased has no key, so her grants show `reason:
// null` with `hasReason: true` — a reason was given, it is gone.
export const supportAccessReviewRowSchema = z.object({
  id: z.string().uuid(),
  employeeId: z.string().uuid(),
  employeeName: z.string().nullable(),
  patientId: z.string().uuid(),
  hasReason: z.boolean(),
  /** The admin's reason, decrypted; null when the patient was erased. */
  reason: z.string().nullable(),
  grantedAt: z.string(),
  expiresAt: z.string(),
  /** Access-log rows written under this grant (basis `support`). */
  accessCount: z.number().int().nonnegative(),
});
export type SupportAccessReviewRow = z.infer<
  typeof supportAccessReviewRowSchema
>;
